import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Star, Moon, Sun, Shield, Gem, ArrowRight, Clock, Send, CheckCircle, Package, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import CrystalQuiz from '../components/CrystalQuiz';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All aligned crystals — every zodiac sign represented + key healing stones
const allCrystals = [
  { name: 'Amethyst', zodiac: 'Aquarius', chakra: 'Crown', color: '#9D4EDD', energy: 'Intuition & Transformation', element: 'Air', symbol: '\u2652' },
  { name: 'Rose Quartz', zodiac: 'Taurus', chakra: 'Heart', color: '#FF6B9D', energy: 'Love & Emotional Healing', element: 'Earth', symbol: '\u2649' },
  { name: 'Carnelian', zodiac: 'Aries', chakra: 'Sacral', color: '#E05533', energy: 'Courage & Vitality', element: 'Fire', symbol: '\u2648' },
  { name: 'Citrine', zodiac: 'Gemini', chakra: 'Solar Plexus', color: '#E6A817', energy: 'Abundance & Joy', element: 'Air', symbol: '\u264A' },
  { name: 'Moonstone', zodiac: 'Cancer', chakra: 'Crown', color: '#A8B8C8', energy: 'Intuition & New Beginnings', element: 'Water', symbol: '\u264B' },
  { name: 'Tiger Eye', zodiac: 'Leo', chakra: 'Solar Plexus', color: '#B8860B', energy: 'Confidence & Willpower', element: 'Fire', symbol: '\u264C' },
  { name: 'Amazonite', zodiac: 'Virgo', chakra: 'Heart & Throat', color: '#3AAFA9', energy: 'Harmony & Truth', element: 'Earth', symbol: '\u264D' },
  { name: 'Lapis Lazuli', zodiac: 'Libra', chakra: 'Third Eye', color: '#1A3C7A', energy: 'Wisdom & Inner Power', element: 'Air', symbol: '\u264E' },
  { name: 'Black Obsidian', zodiac: 'Scorpio', chakra: 'Root', color: '#2C2C2C', energy: 'Protection & Revelation', element: 'Water', symbol: '\u264F' },
  { name: 'Turquoise', zodiac: 'Sagittarius', chakra: 'Throat', color: '#30B0A0', energy: 'Communication & Healing', element: 'Fire', symbol: '\u2650' },
  { name: 'Garnet', zodiac: 'Capricorn', chakra: 'Root', color: '#7B1818', energy: 'Strength & Devotion', element: 'Earth', symbol: '\u2651' },
  { name: 'Aquamarine', zodiac: 'Pisces', chakra: 'Throat', color: '#5BB8D4', energy: 'Calm & Courage', element: 'Water', symbol: '\u2653' },
  { name: 'Selenite', zodiac: null, chakra: 'Crown', color: '#D8D0C8', energy: 'Cleansing & Light', element: 'Air', symbol: null },
  { name: 'Black Tourmaline', zodiac: null, chakra: 'Root', color: '#2D3436', energy: 'Protection & Grounding', element: 'Earth', symbol: null },
  { name: 'Clear Quartz', zodiac: null, chakra: 'Crown', color: '#D0C8E0', energy: 'Amplification & Clarity', element: 'All', symbol: null },
  { name: 'Smokey Quartz', zodiac: null, chakra: 'Root', color: '#6B5B4B', energy: 'Transmutation & Balance', element: 'Earth', symbol: null },
];

const zenMessages = [
  'Returning to the Earth',
  'Being Recharged by the Cosmos',
  'Awaiting Celestial Alignment',
  'Resting in Lunar Light',
  'Gathering Starlight',
  'Absorbing Sacred Frequencies',
  'In Deep Energetic Renewal',
  'Communing with the Source',
  'Bathing in Cosmic Energy',
  'Aligning with the Infinite',
  'Channeling Earth Wisdom',
  'Dancing with the Northern Lights',
  'Drinking from Moonbeams',
  'Absorbing Ancient Vibrations',
  'Dreaming Under Stardust',
  'Soaking in Celestial Waters',
];

const ShopPage = () => {
  usePageTitle('Crystal Shop');
  const [vendorForm, setVendorForm] = useState({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendorSubmitted, setVendorSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  React.useEffect(() => { setIsVisible(true); }, []);

  const handleVendorChange = (e) => {
    setVendorForm({ ...vendorForm, [e.target.name]: e.target.value });
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/vendor-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vendorForm),
      });
      if (res.ok) {
        toast.success('Your inquiry has been received! We will be in touch soon.');
        setVendorSubmitted(true);
        setVendorForm({ name: '', email: '', company: '', message: '' });
      } else {
        throw new Error('Submit failed');
      }
    } catch {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-10 left-1/4 w-96 h-96 bg-[#9D4EDD]/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#c9a96e]/30 rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-10 w-64 h-64 bg-[#FF6B9D]/20 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <Gem className="w-4 h-4 text-[#c9a96e]" />
            <span className="text-sm text-white/80 tracking-widest uppercase">Crystal Sanctuary</span>
          </div>
          <h1 className={`font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Every Stone Finds Its <span className="text-[#c9a96e]">Keeper</span>
          </h1>
          <p className={`text-lg text-white/60 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Our crystals are currently being energetically realigned and recharged.
            Each stone below is destined for its rightful guardian — perhaps that is you.
          </p>
        </div>
      </section>

      {/* Find Your Crystal Quiz */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#faf8f5] to-white" data-testid="quiz-section">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-[#e8e4de]">
          <CrystalQuiz />
        </div>
      </section>

      {/* Crystal Grid — All Aligned Stones */}
      <section className="py-20 px-6" data-testid="crystal-grid-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-[#9D4EDD] font-bold">Your Aligned Crystal</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1e1919] mt-4 mb-4">
              Crystals Aligned to Every Soul
            </h2>
            <p className="text-base text-[#61525a] max-w-2xl mx-auto">
              No matter your sign or your journey, a crystal awaits you.
              These treasures are being sourced and recharged — arriving when the universe deems it time.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {allCrystals.map((crystal, index) => (
              <div
                key={crystal.name}
                className={`group relative bg-white rounded-2xl overflow-hidden border border-[#e8e4de] hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 40 + 300}ms` }}
                data-testid={`crystal-card-${crystal.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {/* Crystal Color Swatch — visual placeholder */}
                <div
                  className="relative h-44 sm:h-52 overflow-hidden flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${crystal.color}18 0%, ${crystal.color}40 50%, ${crystal.color}18 100%)`
                  }}
                >
                  {/* Decorative facets */}
                  <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `linear-gradient(60deg, transparent 40%, ${crystal.color}30 40%, ${crystal.color}30 45%, transparent 45%),
                                     linear-gradient(-60deg, transparent 40%, ${crystal.color}20 40%, ${crystal.color}20 45%, transparent 45%)`
                  }} />

                  {/* Zodiac symbol or gem icon */}
                  <div className="relative z-10 text-center">
                    {crystal.symbol ? (
                      <span className="text-5xl block mb-2 opacity-30 group-hover:opacity-60 transition-opacity" style={{ color: crystal.color }}>
                        {crystal.symbol}
                      </span>
                    ) : (
                      <Gem className="w-12 h-12 mx-auto mb-2 opacity-20 group-hover:opacity-40 transition-opacity" style={{ color: crystal.color }} />
                    )}
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-white/60 shadow-sm">
                      <Clock className="w-3 h-3 text-[#c9a96e]" />
                      <span className="text-[10px] sm:text-xs font-semibold text-[#61525a] uppercase tracking-wider">
                        {zenMessages[index % zenMessages.length]}
                      </span>
                    </div>
                  </div>

                  {/* Subtle shimmer on hover */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 translate-x-[-100%] group-hover:translate-x-[100%]" style={{ transition: 'transform 0.7s ease, opacity 0.3s ease' }} />
                </div>

                {/* Content */}
                <div className="p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: crystal.color }} />
                    <span className="text-[10px] uppercase tracking-wider text-[#7A7A9A]">{crystal.chakra} Chakra</span>
                  </div>
                  <h3 className="font-serif text-base sm:text-lg text-[#1e1919] mb-1">{crystal.name}</h3>
                  <p className="text-xs text-[#9D4EDD] font-medium mb-2">{crystal.energy}</p>
                  {crystal.zodiac && (
                    <div className="flex items-center gap-1.5 text-xs text-[#7A7A9A]">
                      <Star className="w-3 h-3 text-[#c9a96e]" />
                      <span>{crystal.zodiac} Aligned</span>
                    </div>
                  )}
                  {!crystal.zodiac && (
                    <div className="flex items-center gap-1.5 text-xs text-[#7A7A9A]">
                      <Shield className="w-3 h-3 text-[#c9a96e]" />
                      <span>Universal Healer</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-sm text-[#7A7A9A] italic">
              All crystals are ethically sourced and energetically activated before they reach you.
              Availability updates with each lunar cycle.
            </p>
          </div>
        </div>
      </section>

      {/* Sacred Six Collection CTA */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#1e1919] to-[#2a2030]" data-testid="sacred-collection-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a96e]/10 border border-[#c9a96e]/20 mb-6">
              <Sparkles className="w-4 h-4 text-[#c9a96e]" />
              <span className="text-sm text-[#c9a96e] tracking-widest uppercase">Available Now</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-6">
              The <span className="text-[#c9a96e]">Sacred Six</span> Collection
            </h2>
            <p className="text-base text-white/60 max-w-2xl mx-auto">
              While our full crystal shop is being curated, six extraordinary stones have already
              been hand-selected, energetically activated, and are ready for their keepers.
              This is not a shop — it is a sacred exchange.
            </p>
          </div>

          {/* Sacred Six Preview Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { name: 'Master Healer Clear Quartz', subtitle: 'The Stone of Power', price: '$385', color: '#9D4EDD', chakra: 'Crown' },
              { name: 'Royal Amethyst Cluster', subtitle: 'The Stone of Wisdom', price: '$425', color: '#7B2FB5', chakra: 'Third Eye' },
              { name: 'Divine Love Rose Quartz', subtitle: 'Unconditional Love', price: '$275', color: '#FF6B9D', chakra: 'Heart' },
            ].map((stone) => (
              <div key={stone.name} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-[#c9a96e]/30 transition-all" data-testid={`sacred-preview-${stone.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: stone.color }} />
                  <span className="text-xs text-white/40 uppercase tracking-wider">{stone.chakra} Chakra</span>
                </div>
                <h3 className="font-serif text-lg text-white mb-1">{stone.name}</h3>
                <p className="text-sm text-[#c9a96e] mb-3">{stone.subtitle}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-serif text-white">{stone.price}</span>
                  <span className="text-xs text-white/40">Activation included</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/collection"
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-[#c9a96e]/30 hover:scale-105 transition-all duration-300"
              data-testid="view-sacred-six-btn"
            >
              <Gem className="w-5 h-5" />
              Enter the Sacred Collection
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-white/40 text-sm mt-4">
              3 more hand-activated stones await inside
            </p>
          </div>
        </div>
      </section>

      {/* Vendor / Wholesale Section */}
      <section className="py-20 px-6" data-testid="vendor-section">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left — Info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-[#c9a96e]" />
                <span className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-bold">For Vendors</span>
              </div>
              <h2 className="font-serif text-3xl text-[#1e1919] mb-6">
                Sell Your Gemstones <span className="text-[#9D4EDD]">Wholesale</span>
              </h2>
              <p className="text-[#61525a] leading-relaxed mb-6">
                Are you a gemstone miner, collector, or wholesale supplier? We are always
                seeking high-quality, ethically sourced crystals and gemstones to add to our
                curated collection.
              </p>
              <div className="space-y-4 text-sm text-[#61525a]">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#9D4EDD]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Star className="w-4 h-4 text-[#9D4EDD]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1e1919]">Premium Sourcing</p>
                    <p>We purchase raw, polished, and specialty specimens at competitive wholesale prices.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#c9a96e]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Heart className="w-4 h-4 text-[#c9a96e]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1e1919]">Ethical Partnerships</p>
                    <p>We prioritize vendors who share our commitment to sustainable and responsible mining.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#FF6B9D]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Moon className="w-4 h-4 text-[#FF6B9D]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1e1919]">Growing Collection</p>
                    <p>We are expanding our offerings across all chakra-aligned and zodiac-aligned stones.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#e8e4de]">
              {vendorSubmitted ? (
                <div className="text-center py-8" data-testid="vendor-success">
                  <div className="w-16 h-16 rounded-full bg-[#00C853]/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#00C853]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#1e1919] mb-2">Inquiry Received</h3>
                  <p className="text-sm text-[#61525a]">
                    Thank you for reaching out. Our sourcing team will review your inquiry
                    and respond within 48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleVendorSubmit} data-testid="vendor-form">
                  <h3 className="font-serif text-xl text-[#1e1919] mb-1">Wholesale Inquiry</h3>
                  <p className="text-sm text-[#7A7A9A] mb-6">Tell us about your gemstones</p>
                  <div className="space-y-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={vendorForm.name}
                      onChange={handleVendorChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all text-sm"
                      data-testid="vendor-name"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={vendorForm.email}
                      onChange={handleVendorChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all text-sm"
                      data-testid="vendor-email"
                    />
                    <input
                      type="text"
                      name="company"
                      placeholder="Company / Mine Name (optional)"
                      value={vendorForm.company}
                      onChange={handleVendorChange}
                      className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all text-sm"
                      data-testid="vendor-company"
                    />
                    <textarea
                      name="message"
                      placeholder="Tell us about the gemstones you'd like to sell — types, quantities, sourcing location..."
                      value={vendorForm.message}
                      onChange={handleVendorChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none transition-all resize-none text-sm"
                      data-testid="vendor-message"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#9D4EDD]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="vendor-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Submit Wholesale Inquiry
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8]">
        <div className="max-w-2xl mx-auto text-center">
          <Sun className="w-8 h-8 text-[#c9a96e] mx-auto mb-4" />
          <h3 className="font-serif text-xl text-[#1e1919] mb-3">Can't Wait for Your Crystal?</h3>
          <p className="text-sm text-[#61525a] mb-6">
            Our Sacred Six Collection is available now. For custom sourcing of any specific crystal,
            reach out to us directly — we'll find exactly what your soul needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/collection"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-semibold hover:shadow-lg transition-all text-sm"
              data-testid="bottom-sacred-six-btn"
            >
              <Sparkles className="w-4 h-4" />
              Sacred Six Collection
            </Link>
            <Link
              to="/education"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#9D4EDD] text-[#9D4EDD] rounded-xl font-semibold hover:bg-[#9D4EDD] hover:text-white transition-all text-sm"
              data-testid="bottom-contact-btn"
            >
              Contact Our Crystal Experts
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShopPage;
